import React, { useState } from 'react';
import { DigitalCreative } from '../lib/types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Video, Image, MousePointer } from 'lucide-react';

interface DigitalCreativeViewerProps {
  creatives: DigitalCreative[];
  className?: string;
}

export function DigitalCreativeViewer({ creatives, className = '' }: DigitalCreativeViewerProps) {
  const [selectedCreativeId, setSelectedCreativeId] = useState<string>(creatives[0]?.creativeId || '');

  const selectedCreative = creatives.find(c => c.creativeId === selectedCreativeId);

  const getCreativeIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Video className="h-5 w-5 text-blue-600" />;
      case 'image':
        return <Image className="h-5 w-5 text-green-600" />;
      case 'interactive':
        return <MousePointer className="h-5 w-5 text-purple-600" />;
      default:
        return <Image className="h-5 w-5 text-gray-600" />;
    }
  };

  if (!selectedCreative) {
    return <div className={`bg-white p-6 rounded-lg shadow ${className}`}>No creatives available</div>;
  }

  return (
    <div className={`bg-white p-6 rounded-lg shadow ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Digital Creative Viewer</h3>
        <p className="text-sm text-gray-600 mb-4">Filter by creative and see who has viewed each one</p>
        
        <Select value={selectedCreativeId} onValueChange={setSelectedCreativeId}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {creatives.map((creative) => (
              <SelectItem key={creative.creativeId} value={creative.creativeId}>
                {creative.creativeName} ({creative.creativeType})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            {getCreativeIcon(selectedCreative.creativeType)}
            <div>
              <CardTitle>{selectedCreative.creativeName}</CardTitle>
              <CardDescription>
                {selectedCreative.campaignName} • {selectedCreative.creativeType.charAt(0).toUpperCase() + selectedCreative.creativeType.slice(1)}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Impressions</p>
              <p className="text-2xl font-bold text-gray-900">{selectedCreative.impressions.toLocaleString()}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Unique Viewers</p>
              <p className="text-2xl font-bold text-gray-900">{selectedCreative.uniqueViewers.toLocaleString()}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Avg View Duration</p>
              <p className="text-2xl font-bold text-gray-900">{selectedCreative.avgViewDuration.toFixed(1)}s</p>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Viewer Demographics by Age</h4>
            <div className="space-y-3">
              {selectedCreative.ageGroups.map((group) => {
                const percentage = ((group.count / selectedCreative.uniqueViewers) * 100).toFixed(1);
                return (
                  <div key={group.ageRange}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">{group.ageRange}</span>
                      <span className="text-gray-900 font-medium">{group.count.toLocaleString()} ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

